import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/layout/AdminLayout';
import {
  View,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Platform,
  Text,
} from 'react-native';
import { H1, ButtonText } from '../../components/common/TextStyles';
import { getColor, getSpacing, getBorderRadius } from '../theme/adminTheme';
import Table from '../components/common/Table';
import { Ionicons } from '@expo/vector-icons';
import { getApiUrl } from '../../config/development';
import AsyncStorage from '@react-native-async-storage/async-storage';

const InspirationsManageScreen = ({ navigation }) => {
  const [inspirations, setInspirations] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (date instanceof Date && !isNaN(date)) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      return '—';
    } catch (error) {
      console.error('Date formatting error:', error);
      return '—';
    }
  };

  const processInspirationsData = (data) => {
    console.log('[InspirationsManage] Processing raw data:', {
      count: data?.length,
      sample: data?.[0]
    });

    if (!Array.isArray(data)) return [];

    // Data already comes grouped by date with verse and video properties
    const processed = data
      .map(item => ({
        ...item,
        id: item.id || `${item.display_date}-group`,
      }))
      .sort((a, b) => new Date(b.display_date) - new Date(a.display_date));

    console.log('[InspirationsManage] Processed data:', {
      count: processed.length,
      sample: processed[0] ? {
        date: processed[0].display_date,
        verseContent: processed[0].verse?.content?.substring(0, 50),
        videoContent: processed[0].video?.content?.substring(0, 50),
        itemCount: processed[0].items?.length
      } : null
    });

    return processed;
  };

  const fetchInspirations = useCallback(async () => {
    try {
      const token = await AsyncStorage.getItem('adminAuthToken') || await AsyncStorage.getItem('token');
      if (!token) {
        throw new Error('No auth token found');
      }

      console.log('[InspirationsManage] Fetching inspirations...');
      const response = await fetch(`${getApiUrl()}/inspirations/admin`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[InspirationsManage] Raw response:', {
        status: response.status,
        dataLength: data?.length,
        firstItem: data?.[0]
      });

      const processedData = processInspirationsData(data);
      setInspirations(processedData);
    } catch (error) {
      console.error('[InspirationsManage] Fetch error:', error);
      Alert.alert('Error', 'Failed to fetch inspirations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInspirations();
  }, [fetchInspirations]);

  const confirmDelete = (inspiration) => {
    const message = `Are you sure you want to delete this ${inspiration.content_type}?`;

    if (Platform.OS === 'web') {
      if (window.confirm(message)) {
        handleDelete(inspiration);
      }
    } else {
      Alert.alert(
        'Delete Inspiration',
        message,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            onPress: () => handleDelete(inspiration),
            style: 'destructive'
          },
        ]
      );
    }
  };

  const handleDelete = async (inspiration) => {
    try {
      const token = await AsyncStorage.getItem('adminAuthToken') || await AsyncStorage.getItem('token');
      if (!token) {
        throw new Error('No auth token found');
      }

      console.log(`[InspirationsManage] Deleting ${inspiration.content_type} with ID: ${inspiration?.id}`);

      const response = await fetch(`${getApiUrl()}/inspirations/admin/${inspiration?.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        if (Platform.OS === 'web') {
          window.alert('Inspiration deleted successfully');
        } else {
          Alert.alert('Success', 'Inspiration deleted successfully');
        }
        fetchInspirations();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('[InspirationsManage] Delete failed:', errorData);
        const errorMsg = errorData.message || 'Failed to delete inspiration';
        if (Platform.OS === 'web') {
          window.alert(`Error: ${errorMsg}`);
        } else {
          Alert.alert('Error', errorMsg);
        }
      }
    } catch (error) {
      console.error('Error deleting inspiration:', error);
      if (Platform.OS === 'web') {
        window.alert(`Error: ${error.message || 'Failed to delete inspiration'}`);
      } else {
        Alert.alert('Error', 'Failed to delete inspiration');
      }
    }
  };

  return (
    <AdminLayout>
      <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
        <View style={styles.header}>
          <H1>Daily Inspirations</H1>
          <TouchableOpacity
            style={styles.addButton}
            onPress={() => navigation.navigate('InspirationAdd')}
          >
            <Ionicons name="add" size={20} color="#FFFFFF" />
            <ButtonText style={styles.addButtonText}>Add New</ButtonText>
          </TouchableOpacity>
        </View>

        <View style={styles.tableContainer}>
          <Table
            data={inspirations || []}
            columns={[
              {
                header: 'Date',
                field: 'display_date',
                width: '15%',
                render: (value) => <Text style={styles.dateText}>{formatDate(value)}</Text>,
              },
              {
                header: 'Verse Content',
                field: 'verse',
                width: '45%',
                render: (verse) => {
                  if (!verse) return <Text style={styles.emptyText}>—</Text>;

                  return (
                    <View style={styles.contentContainer}>
                      <Text style={styles.contentText} numberOfLines={2}>
                        {verse.content || '—'}
                      </Text>
                      {verse.background_color && (
                        <Text style={[styles.contentText, styles.subText]}>
                          Bg: {verse.background_color}
                        </Text>
                      )}
                    </View>
                  );
                },
              },
              {
                header: 'Video Content',
                field: 'video',
                width: '20%',
                render: (video) => {
                  if (!video) return <Text style={styles.emptyText}>—</Text>;
                  return (
                    <View style={styles.contentContainer}>
                      <Text style={styles.contentText} numberOfLines={2}>
                        {video.content || '—'}
                      </Text>
                    </View>
                  );
                },
              },
              {
                header: 'Actions',
                field: 'id',
                width: '20%',
                render: (_, item) => (
                  <View style={styles.actionButtons}>
                    {item.verse && (
                      <TouchableOpacity
                        style={styles.deleteButton}
                        onPress={() => confirmDelete(item.verse)}
                      >
                        <Ionicons name="trash-outline" size={14} color={getColor('danger.600')} />
                        <Text style={styles.deleteButtonText}>Delete Verse</Text>
                      </TouchableOpacity>
                    )}
                    {item.video && (
                      <TouchableOpacity
                        style={styles.deleteButton}
                        onPress={() => confirmDelete(item.video)}
                      >
                        <Ionicons name="trash-outline" size={14} color={getColor('danger.600')} />
                        <Text style={styles.deleteButtonText}>Delete Video</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                ),
              }
            ]}
            loading={loading}
          />
        </View>
      </ScrollView>
    </AdminLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f3f4f6',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: getSpacing('lg'),
    paddingBottom: getSpacing('md'),
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: getColor('primary.600'),
    paddingHorizontal: getSpacing('md'),
    paddingVertical: getSpacing('sm'),
    borderRadius: getBorderRadius('md'),
    gap: getSpacing('xs'),
  },
  addButtonText: {
    color: getColor('background.default'),
  },
  tableContainer: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    padding: getSpacing('md'),
    borderRadius: getBorderRadius('lg'),
    overflow: 'hidden',
  },
  actionButtons: {
    flexDirection: 'column',
    gap: getSpacing('xs'),
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: getSpacing('sm'),
    paddingVertical: 8,
    gap: getSpacing('xs'),
    backgroundColor: getColor('danger.50'),
    borderRadius: getBorderRadius('md'),
    borderWidth: 1,
    borderColor: getColor('danger.100'),
  },
  deleteButtonText: {
    color: getColor('danger.600'),
    fontSize: 12,
    fontWeight: '600',
  },
  contentContainer: {
    padding: getSpacing('xs'),
    backgroundColor: getColor('gray.50'),
    borderRadius: getBorderRadius('md'),
    borderWidth: 1,
    borderColor: getColor('gray.200'),
  },
  contentText: {
    color: getColor('gray.900'),
    fontSize: 14,
    lineHeight: 20,
  },
  dateText: {
    color: getColor('gray.900'),
    fontSize: 14,
    fontWeight: '500',
  },
  subText: {
    fontSize: 11,
    color: getColor('gray.500'),
    marginTop: getSpacing('xs'),
  },
  emptyText: {
    color: getColor('gray.400'),
    fontStyle: 'italic',
    fontSize: 13,
  },
});

export default InspirationsManageScreen;
